import type { Questions, QuizAnswer } from '~/models'

export default function useQuizQuestions() {
  const { t } = useI18n()
  const { fetchAuth, addErrorToast, addSuccessToast, addUndoSaveToast } =
    useHelpers()
  const router = useRouter()
  const isLoading = ref(true)

  const { originalQuestions } = storeToRefs(useUserStore())

  const startQuizAttempt = async ({
    uuid,
    forceNew = false
  }: {
    uuid: string
    forceNew: boolean
  }) => {
    const { data, error } = await fetchAuth(
      `/concepts/${uuid}/start-attempt/`,
      {
        fetchOptions: { method: 'POST', body: { force_new: forceNew } }
      }
    )

    if (error) {
      if (error.statusCode === 409) {
        return {
          message: 'test-already-started',
          uuid: error.data.meta.unfinished_attempt.uuid
        }
      }
    }

    router.push(`/test/${data.data.uuid}`)
  }

  const getNextQuestion = (uuid: string) => {
    return fetchAuth(`/attempts/${uuid}/get-next-question/`)
  }

  const postAnswerQuestion = ({
    uuid,
    payload
  }: {
    uuid: string
    payload: QuizAnswer
  }) => {
    return fetchAuth(`/attempts/${uuid}/answer-question/`, {
      fetchOptions: { method: 'POST', body: payload }
    })
  }

  const getDraftQuizQuestions = async (uuid: string) => {
    const { data } = await fetchAuth(`/manage/drafts/concepts/${uuid}/quizzes`)
    return data
  }

  const saveDraftQuizQuestions = async ({
    uuid,
    payload
  }: {
    uuid: string
    payload: Questions[]
  }) => {
    return await fetchAuth(`/manage/drafts/concepts/${uuid}/quizzes`, {
      fetchOptions: { method: 'POST', body: payload }
    })
  }

  const saveQuizQuestions = async (
    concept: string,
    questions: { value: Questions[] }
  ) => {
    const { error } = await fetchAuth(
      `/manage/concepts/${concept}/upsert-quiz`,
      {
        fetchOptions: { method: 'PUT', body: questions }
      }
    )

    if (error) {
      addErrorToast()
      return
    }

    router.push('/teacher')
    addUndoSaveToast(t('p_Questions.Test questions saved'), 'quiz')
  }

  const getQuizQuestions = async (uuid: string) => {
    const { data, error } = await fetchAuth(
      `/manage/quiz-questions?concept=${uuid}&include_default=true`
    )

    if (error) {
      router.push('/teacher')
      isLoading.value = false
      if (error.statusCode !== 401) {
        addErrorToast(t('c_Toast.Something went wrong, try again'))
      }
      return
    }
    isLoading.value = false
    return data.data
  }

  async function resetQuiz() {
    // There originally was no quiz when reseting, thus deleting the newly created one
    if (
      originalQuestions.value.payload.questions.length === 1 &&
      originalQuestions.value.payload.questions[0].content === ''
    ) {
      const { error } = await fetchAuth(
        `/manage/concepts/${originalQuestions.value.uuid}/delete-quiz`,
        {
          fetchOptions: { method: 'DELETE' }
        }
      )
      if (error) {
        addErrorToast()
        return
      }
    } else {
      const { error } = await fetchAuth(
        `/manage/concepts/${originalQuestions.value.uuid}/upsert-quiz`,
        {
          fetchOptions: { method: 'PUT', body: originalQuestions.value.payload }
        }
      )
      if (error) {
        addErrorToast()
        return
      }
    }

    router.push(`/teacher/test-questions/${originalQuestions.value.uuid}/edit`)
    addSuccessToast(t('p_Teacher.Questions reset successfully'))
    originalQuestions.value = null
  }

  return {
    startQuizAttempt,
    getNextQuestion,
    postAnswerQuestion,
    getDraftQuizQuestions,
    saveDraftQuizQuestions,
    saveQuizQuestions,
    getQuizQuestions,
    resetQuiz,
    isLoading
  }
}
